import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { moroccoRegions, allPartiesIncludingNonParliamentary } from "@/data/parties";
import { CandidateData } from "@/components/CandidateFormDialog";
import { UserPlus, Image as ImageIcon, MapPin, Share2, Info, Building } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CandidateData & { partyAcronym: string }, status: "draft" | "published") => void;
  initialData?: (CandidateData & { partyAcronym: string }) | null;
}

export default function AdminCandidateFormDialog({ isOpen, onClose, onSave, initialData }: Props) {
  const [formData, setFormData] = useState<Partial<CandidateData> & { partyAcronym?: string }>({
    listType: "locale",
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        setFormData(initialData);
      } else {
        setFormData({ listType: "locale" });
      }
    }
  }, [isOpen, initialData]);

  const regionsProvinces = moroccoRegions.find(r => r.name === formData.region)?.provinces || [];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, photoUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e: React.FormEvent, status: "draft" | "published" = "published") => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName || !formData.region || !formData.partyAcronym) {
      alert("Veuillez remplir tous les champs obligatoires (Prénom, Nom, Région, Parti).");
      return;
    }

    onSave({
      id: initialData?.id || Math.random().toString(36).substr(2, 9),
      firstName: formData.firstName || "",
      lastName: formData.lastName || "",
      photoUrl: formData.photoUrl || "",
      region: formData.region || "",
      province: formData.province || "",
      listType: formData.listType as "locale" | "regionale",
      bio: formData.bio || "",
      facebook: formData.facebook || "",
      twitter: formData.twitter || "",
      instagram: formData.instagram || "",
      website: formData.website || "",
      partyAcronym: formData.partyAcronym || "",
      email: formData.email || "",
      whatsapp: formData.whatsapp || "",
    }, status);

    setFormData({ listType: "locale" });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[720px] w-[95vw] rounded-3xl p-0 shadow-2xl overflow-hidden border-none max-h-[90vh] flex flex-col">
        <div className="h-2 w-full bg-gradient-to-r from-violet-600 via-primary to-accent shrink-0" />

        <DialogHeader className="px-6 pt-6 pb-4 shrink-0 border-b border-border/50">
          <DialogTitle className="text-2xl font-display text-violet-700 flex items-center gap-2">
            <UserPlus className="w-6 h-6" /> {initialData ? "Modifier un candidat (Admin)" : "Ajouter un candidat (Admin)"}
          </DialogTitle>
          <DialogDescription className="font-body text-muted-foreground mt-1">
            Ajoutez un candidat pour n'importe quel parti politique enregistré sur la plateforme.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-6">
          <form id="admin-candidate-form" className="space-y-8">

            {/* Section 0 : Sélection du parti (spécifique admin) */}
            <section className="space-y-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2 border-b border-violet-200 pb-2">
                <Building className="w-4 h-4 text-violet-600" />
                <span className="text-violet-700">0. Parti concerné</span>
                <span className="ml-2 text-xs bg-violet-100 text-violet-600 px-2 py-0.5 rounded-full font-bold">Réservé à l'Admin</span>
              </h3>
              <div className="space-y-2">
                <label className="text-sm font-medium">Parti politique *</label>
                <Select
                  value={formData.partyAcronym}
                  onValueChange={(val) => setFormData({ ...formData, partyAcronym: val })}
                  required
                >
                  <SelectTrigger className="w-full h-11 rounded-xl border-violet-200 focus:ring-violet-300">
                    <SelectValue placeholder="Sélectionnez un parti..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[280px]">
                    {allPartiesIncludingNonParliamentary.map(p => (
                      <SelectItem key={p.acronym} value={p.acronym}>
                        <span className="font-bold">{p.acronym}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Ce candidat sera rattaché au parti sélectionné et visible dans son espace.</p>
              </div>
            </section>

            {/* Section 1 : Informations personnelles */}
            <section className="space-y-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2 border-b border-border/50 pb-2">
                <Info className="w-4 h-4 text-primary" /> 1. Informations Personnelles
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Prénom *</label>
                  <input type="text" name="firstName" value={formData.firstName || ""} onChange={handleChange} required className="w-full h-11 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nom *</label>
                  <input type="text" name="lastName" value={formData.lastName || ""} onChange={handleChange} required className="w-full h-11 rounded-xl border border-input shadow-sm bg-background px-4 focus:ring-2 focus:ring-primary/20 outline-none" />
                </div>
              </div>
              <div className="space-y-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-muted-foreground" /> Photo du candidat
                </label>
                <div className="flex items-center gap-3">
                  {formData.photoUrl && (
                    <img src={formData.photoUrl} alt="Aperçu" className="w-11 h-11 rounded-xl object-cover shrink-0 border border-border/50" />
                  )}
                  <div className="flex-1 space-y-2">
                    <input type="url" name="photoUrl" placeholder="URL de l'image (ex: https://...)" value={formData.photoUrl || ""} onChange={handleChange} className="w-full h-9 rounded-lg border border-input bg-background px-3 text-xs focus:ring-2 focus:ring-primary/20 outline-none" />
                    <div className="flex items-center gap-2">
                      <div className="h-px bg-border flex-1"></div>
                      <span className="text-[10px] text-muted-foreground uppercase font-bold">OU</span>
                      <div className="h-px bg-border flex-1"></div>
                    </div>
                    <input type="file" accept="image/*" onChange={handlePhotoChange} className="w-full h-9 rounded-lg border border-input shadow-sm bg-background px-2 py-1.5 text-xs focus:ring-2 focus:ring-primary/20 file:mr-2 file:py-1 file:px-2 file:rounded-full file:border-0 file:text-[10px] file:font-semibold file:bg-primary/10 file:text-primary hover:file:bg-primary/20" />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Présentation (Biographie) *</label>
                <textarea name="bio" rows={4} value={formData.bio || ""} onChange={handleChange} required className="w-full rounded-xl border border-input shadow-sm bg-background p-4 focus:ring-2 focus:ring-primary/20 resize-none font-body text-sm outline-none" />
              </div>
            </section>

            {/* Section 2 : Circonscription */}
            <section className="space-y-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2 border-b border-border/50 pb-2">
                <MapPin className="w-4 h-4 text-primary" /> 2. Circonscription
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Région *</label>
                  <Select value={formData.region} onValueChange={(val) => setFormData({ ...formData, region: val, province: "" })} required>
                    <SelectTrigger className="w-full h-11 rounded-xl">
                      <SelectValue placeholder="Sélectionnez..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[60vh] overflow-y-auto">
                      {moroccoRegions.map(r => <SelectItem key={r.name} value={r.name}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Province / Préfecture</label>
                  <Select value={formData.province} onValueChange={(val) => setFormData({ ...formData, province: val })} disabled={!formData.region}>
                    <SelectTrigger className="w-full h-11 rounded-xl">
                      <SelectValue placeholder="Sélectionnez..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[60vh] overflow-y-auto">
                      {regionsProvinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2 md:w-1/2">
                <label className="text-sm font-medium">Type de Liste *</label>
                <Select value={formData.listType} onValueChange={(val: "locale" | "regionale") => setFormData({ ...formData, listType: val })}>
                  <SelectTrigger className="w-full h-11 rounded-xl">
                    <SelectValue placeholder="Sélectionnez..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="locale">Liste Locale (Tête de liste)</SelectItem>
                    <SelectItem value="regionale">Liste Régionale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </section>

            {/* Section 3 : Réseaux sociaux & Contact */}
            <section className="space-y-4">
              <h3 className="font-semibold text-foreground flex items-center gap-2 border-b border-border/50 pb-2">
                <Share2 className="w-4 h-4 text-primary" /> 3. Contact & Réseaux Sociaux
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Email de contact</label>
                  <input type="email" name="email" placeholder="contact@candidat.ma" value={formData.email || ""} onChange={handleChange} className="w-full h-11 rounded-xl border border-input bg-background px-4 focus:ring-2 focus:ring-primary/20 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#25D366]">Numéro WhatsApp (Optionnel)</label>
                  <input type="tel" name="whatsapp" pattern="^[+]?[0-9]{10,}$" title="num incorrect" placeholder="+212 6..." value={formData.whatsapp || ""} onChange={handleChange} className="w-full h-11 rounded-xl border border-input bg-background px-4 focus:ring-2 focus:ring-[#25D366]/20 outline-none" />
                  <p className="text-[10px] text-muted-foreground leading-tight">
                    *L'ajout d'un numéro WhatsApp n'engage que la responsabilité du candidat.
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#1877F2]">Facebook</label>
                  <input type="url" name="facebook" placeholder="https://facebook.com/..." value={formData.facebook || ""} onChange={handleChange} className="w-full h-11 rounded-xl border border-input bg-background px-4 focus:ring-2 focus:ring-[#1877F2]/20 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">X</label>
                  <input type="url" name="twitter" placeholder="https://x.com/..." value={formData.twitter || ""} onChange={handleChange} className="w-full h-11 rounded-xl border border-input bg-background px-4 focus:ring-2 focus:ring-foreground/20 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-[#E4405F]">Instagram</label>
                  <input type="url" name="instagram" placeholder="https://instagram.com/..." value={formData.instagram || ""} onChange={handleChange} className="w-full h-11 rounded-xl border border-input bg-background px-4 focus:ring-2 focus:ring-[#E4405F]/20 outline-none" />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Site Officiel</label>
                  <input type="url" name="website" placeholder="https://www.exemple.ma" value={formData.website || ""} onChange={handleChange} className="w-full h-11 rounded-xl border border-input bg-background px-4 focus:ring-2 focus:ring-primary/20 outline-none" />
                </div>
              </div>
              <div className="bg-orange-500 border border-orange-600 rounded-xl p-3 mt-4 text-white text-sm text-center shadow-sm">
                <strong className="font-bold mb-0.5 block">⚠️ Avertissement</strong> 
                Le partage des réseaux sociaux et informations personnelles (notamment l'email et le WhatsApp) n'engage que la responsabilité du candidat et du parti.
              </div>
            </section>
          </form>
        </div>

        <div className="p-6 border-t border-border/50 bg-muted/20 shrink-0 flex justify-between items-center gap-3">
          <p className="text-xs text-muted-foreground font-body">
            * Champs obligatoires. Ce candidat sera marqué comme <strong>ajouté par Admin</strong>.
          </p>
          <div className="flex gap-2 shrink-0">
            <Button type="button" variant="outline" onClick={onClose} className="h-11 rounded-xl px-6">Annuler</Button>
            {!initialData && <Button type="button" variant="outline" className="h-11 rounded-xl px-6 border-orange-200 text-orange-600 bg-orange-50 hover:bg-orange-100" onClick={(e) => handleSubmit(e, "draft")}>Enregistrer Brouillon</Button>}
            <Button type="button" onClick={(e) => handleSubmit(e, "published")} className="h-11 rounded-xl px-8 font-bold bg-violet-600 hover:bg-violet-700 text-white shadow-md hover:scale-105 transition-transform">
              {initialData ? "Enregistrer les modifications" : "Publier le candidat"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
